import { useEffect, FC, useCallback, useState, memo, useMemo } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import randomstring from 'randomstring';
import graphql from 'babel-plugin-relay/macro';

import AddPhotoIcon from '@mui/icons-material/AddAPhoto';
import Box from '@mui/material/Box';
import DropZone from 'components/DropZone';
import Toolbar from 'components/MediaEditorToolbar';
import Sort from 'components/MediaEditorSort';
import Previews from 'components/MediaEditorPreviews';

import { deleteNode, useErrorHandler } from 'lib';

import { MediaEditorUploadMutation } from '__generated__/MediaEditorUploadMutation.graphql';
import { MediaEditorDeleteMutation } from '__generated__/MediaEditorDeleteMutation.graphql';
import { MediaEditor_mediable$key } from '__generated__/MediaEditor_mediable.graphql';

export type GalleryItem = {
  file: File;
  progress: number;
};

type Props = {
  mediable: MediaEditor_mediable$key;
  limit?: number;
  disabled?: boolean;
  multiple?: boolean;
};

type PreviewsState = {
  [key: string]: GalleryItem;
};

const connection = 'MediaEditor_mediable_media';

const MediaEditor: FC<Props> = props => {
  const { limit, disabled = false, multiple = true } = props;
  const [sorting, setSorting] = useState(false);
  const errorHandler = useErrorHandler();

  const mediable = useFragment(
    graphql`
      fragment MediaEditor_mediable on Mediable {
        id

        media(first: 2147483647)
          @connection(key: "MediaEditor_mediable_media") {
          edges {
            node {
              id
            }
          }

          ...MediaEditorPreviews_media
          ...MediaEditorSort_media
        }
      }
    `,
    props.mediable
  );

  const { id, media } = mediable;

  const [upload, uploading] = useMutation<MediaEditorUploadMutation>(graphql`
    mutation MediaEditorUploadMutation(
      $mediableId: ID!
      $position: Int
      $file: Upload!
      $connections: [ID!]!
    ) {
      uploadMedia(mediableId: $mediableId, position: $position, file: $file) {
        node @appendNode(connections: $connections, edgeTypeName: "MediaEdge") {
          id
          ...MediaEditorThumb_media
        }
      }
    }
  `);

  const [deleteMedia, deleting] =
    useMutation<MediaEditorDeleteMutation>(graphql`
      mutation MediaEditorDeleteMutation($id: ID!, $connections: [ID!]!) {
        deleteNode(id: $id) @deleteEdge(connections: $connections)
      }
    `);

  const [previews, setPreviews] = useState<PreviewsState>({});

  const [selected, setSelected] = useState<string[]>([]);

  const preview = useMemo(() => {
    const items = [];

    for (let hash in previews) {
      items.push(previews[hash]);
    }

    return items;
  }, [previews]);

  useEffect(() => {
    setPreviews({});
  }, []);

  const handleDrop = useCallback(
    (accepted: File[]) => {
      accepted.forEach((file, i) => {
        const hash = randomstring.generate();
        const connectionID = ConnectionHandler.getConnectionID(id, connection);
        const position = media?.edges.length ?? 0 + i;

        setPreviews(current => ({
          ...current,
          [hash]: { file, progress: 0 },
        }));

        upload({
          variables: {
            connections: [connectionID],
            position,
            mediableId: id,
            file: null,
          },
          uploadables: { file },

          ...errorHandler({
            onCompleted: () => {
              setPreviews(current => {
                const newState = { ...current };
                delete newState[hash];
                return newState;
              });
            },
          }),
        });
      });
    },
    [errorHandler, id, media?.edges.length, upload]
  );

  const handleSelectAll = useCallback(() => {
    if (selected.length > 0) {
      setSelected([]);
    } else {
      setSelected(media?.edges.map(({ node }) => node.id) ?? []);
    }
  }, [media?.edges, selected.length]);

  const deleteSingleMedia = useCallback(
    (mediaID: string) => {
      const connectionID = ConnectionHandler.getConnectionID(id, connection);

      deleteMedia({
        variables: {
          connections: [connectionID],
          id: mediaID,
        },

        ...errorHandler(),

        optimisticUpdater: store => {
          deleteNode(store, id, connection, mediaID);
        },
      });
    },
    [id, deleteMedia, errorHandler]
  );

  const handleDeleteAll = useCallback(() => {
    setSelected([]);

    selected.forEach(mediaID => {
      deleteSingleMedia(mediaID);
    });
  }, [selected, deleteSingleMedia]);

  const handleDelete = useCallback(
    (mediaID: string) => {
      setSelected(current => current.filter(item => item !== mediaID));

      deleteSingleMedia(mediaID);
    },
    [deleteSingleMedia]
  );

  const count = media?.edges.length ?? 0 + preview.length;

  return (
    <Box
      sx={{
        width: '100%',
      }}>
      {!disabled && !(limit && count >= limit) && (
        <DropZone
          multiple={multiple}
          disabled={disabled}
          icon={<AddPhotoIcon fontSize="small" />}
          text="Tempkite nuotraukas arba spauskite čia, kad pradėti nuotraukų įkėlimą."
          onDrop={handleDrop}
        />
      )}

      {limit !== 1 && !disabled && (
        <Toolbar
          total={media?.edges.length}
          selected={selected.length}
          loading={sorting || deleting || uploading}
          onSelectAll={handleSelectAll}
          onDelete={handleDeleteAll}
        />
      )}

      <Sort mediableId={mediable.id} media={media} onStatusChange={setSorting}>
        <Previews
          media={media}
          preview={preview}
          selected={selected}
          disabled={disabled}
          onSelect={setSelected}
          onDelete={handleDelete}
        />
      </Sort>
    </Box>
  );
};

export default memo(MediaEditor);
